import { useVisitDetailStore } from "@/store/visits/visitDetail";
import { useVisitStore } from "@/store/visits/visitStore";
import { VisitDetailModal } from "@/components/shared/visits/visit-detail-modal";
import {
  useVisitIssueStore,
  VisitIssueType,
} from "@/store/report-issue/visit-issue/visitIssue";
import { VisitRateModalContainer } from "../visit-rate";
import { useVisitTokenStore } from "@/store/visits/visitTokenStore";
import { RescheduleSkipVisitConfirmation } from "../reschedule-visit/reschedule-visit-confirmation";
import { LoadingDialog } from "@/components/shared/loading-dialog";
import type { VisitDetailData } from "@/components/shared/visits/visit-detail";
import { ContactUsToRescheduleModal } from "../contact-us-to-reschedule";
import { VisitSurveySuccessModal } from "../visit-survey-success";
import { SkipVisitModalContainer } from "../skip-visit";
import { useConfirmations } from "@/components/hooks/use-confirmations";
import { useCancelVisitWithTokenStore } from "@/store/visits/useCancelVisitWithToken";
import { useClientCancelVisitStore } from "@/store/visits/useClientCancelVisit";
import { CancelVisitReasonEnum } from "@/__generated__/graphql";
import { getErrorMessage } from "@/lib/helpers/string";
import type { GraphQLFormattedError } from "graphql";
import { getRequiredConfirmations } from "@/lib/request-factory";
import { showToast } from "@/components/ui/toast/show-toast";
import { useState } from "react";
import { useVisitAction } from "../use-visit-action";

export function VisitDetailContainer({
  type = "auth",
}: {
  type?: "token" | "auth";
}) {
  const {
    data: {
      showModal,
      showRateVisitModal,
      showSkipVisitModal,
      showRescheduleVisitConfirmation,
      showContactSupportToRescheduleModal,
      showSkipVisitSurveySuccess,
      visitToken,
    },
    closeVisitModal,
    openRescheduleVisitConfirmation,
    openContactSupportToRescheduleModal,
    openSkipVisitConfirmationModal,
    openSkipVisitModalById,
  } = useVisitDetailStore();

  const { fetch: cancelWithToken, loading: cancelWithTokenLoading } =
    useCancelVisitWithTokenStore();
  const { fetch: cancelVisit, loading: cancelVisitLoading } =
    useClientCancelVisitStore();
  const loadingCancelVisit = cancelWithTokenLoading || cancelVisitLoading;

  const {
    updateConfirmations,
    setConfirmations,
    confirmations,
    firstUnconfirmed,
  } = useConfirmations();

  const {
    handleRateVisit: actionHandleRateVisit,
    handleReportVisit: actionHandleReportVisit,
  } = useVisitAction();

  const openVisitIssueModal = useVisitIssueStore(
    (state) => state.openVisitIssueModal,
  );
  const { data, loading } =
    type === "token" ? useVisitTokenStore() : useVisitStore();

  const handleReschedule = (visitId: string) => {
    // allowReschedule present since we only allow reschedule for ad-hoc and yearly packages
    // see issue #4373 for more details
    if (data?.clientCanReschedule && data?.allowReschedule) {
      openRescheduleVisitConfirmation(
        visitId,
        type === "token" && visitToken ? visitToken : undefined,
      );
    } else {
      openContactSupportToRescheduleModal();
    }
  };

  const responseSkipVisit = (res: {
    data: boolean | null;
    error: string | null;
    graphqlErrors?: ReadonlyArray<GraphQLFormattedError>;
  }) => {
    setSkipVisitButtonLoading(false);
    if (res.error && res.graphqlErrors) {
      const confirmations = getRequiredConfirmations(res.graphqlErrors);
      return { success: false, confirmations, error: res.error };
    }

    return { success: Boolean(res.data), confirmations: [], error: res.error };
  };

  const handleChangeWorker = (visitId: string) => {
    openVisitIssueModal(VisitIssueType.changeVisitWorker, visitId);
  };

  const handleReportVisit = (visitId: string) => {
    closeVisitModal();
    actionHandleReportVisit(visitId);
  };

  const handleSkipVisitConfirmation = (visitId: string) => {
    openSkipVisitConfirmationModal();
    handleReschedule(visitId);
  };

  const skipVisit = async () => {
    setSkipVisitButtonLoading(true);
    const visitId = data?.id;
    const visitToken = data?.token;

    try {
      if (!visitToken && visitId) {
        return await cancelVisit(
          {
            requestPayload: {
              input: {
                visitId,
                reason: CancelVisitReasonEnum.CancelledByClient,
                confirmations,
              },
            },
          },
          {
            selfHandleError: true,
          },
        ).then(responseSkipVisit);
      } else {
        return await cancelWithToken(
          {
            requestPayload: {
              input: {
                token: visitToken || "",
                reason: CancelVisitReasonEnum.CancelledByClient,
                confirmations,
              },
            },
          },
          {
            selfHandleError: true,
          },
        ).then(responseSkipVisit);
      }
    } catch (error) {
      const errMessage = getErrorMessage(error, "Failed to cancel visit");
      showToast({ type: "error", title: errMessage });
      setSkipVisitButtonLoading(false);

      return {
        success: false,
        confirmations: [],
        error: "Failed to cancel visit",
      };
    }
  };

  const [skipVisitButtonLoading, setSkipVisitButtonLoading] = useState(false);
  const handleSkipVisit = async (
    confirmedKey: string | undefined = undefined,
    confirmed = false,
  ): Promise<void> => {
    if (confirmedKey) {
      updateConfirmations(confirmedKey, confirmed);
    }

    const {
      success,
      error,
      confirmations: requiredConfirmations,
    } = await skipVisit();

    if (requiredConfirmations.length && data) {
      setConfirmations(requiredConfirmations);
      openSkipVisitModalById(data.id);
      return;
    }

    if (!success && error) {
      showToast({ type: "error", title: error });
      return;
    }

    showToast({ type: "success", title: "Success cancel visit!" });
    closeVisitModal();
  };

  const otherModalOpen =
    showSkipVisitModal ||
    showRateVisitModal ||
    showRescheduleVisitConfirmation ||
    showContactSupportToRescheduleModal;

  if (showContactSupportToRescheduleModal) {
    return <ContactUsToRescheduleModal />;
  }

  if ((otherModalOpen && loading) || loadingCancelVisit) {
    return <LoadingDialog open />;
  }

  if (showRateVisitModal) {
    return <VisitRateModalContainer />;
  }

  if (showRescheduleVisitConfirmation && data) {
    return (
      <>
        <RescheduleSkipVisitConfirmation
          visitId={data.id}
          department={data.department}
          visitStatus={data.status}
          serviceDate={data.serviceDate}
          hasWorkerAssignment={!!data.worker}
          serviceType={data.serviceType}
          onClickSkipVisit={() => handleSkipVisit()}
          skipVisitButtonLoading={skipVisitButtonLoading}
        />
      </>
    );
  }

  // final confirm modal
  if (showSkipVisitModal && data && firstUnconfirmed) {
    return (
      <SkipVisitModalContainer
        loading={skipVisitButtonLoading}
        onConfirm={() => handleSkipVisit(firstUnconfirmed?.key, true)}
        onCancel={() => {
          openRescheduleVisitConfirmation(data.id);
        }}
        firstUnconfirmed={firstUnconfirmed}
      />
    );
  }

  if (showModal) {
    return (
      <VisitDetailModal
        handleClose={closeVisitModal}
        visitData={data as VisitDetailData}
        loading={loading}
        handleReschedule={handleReschedule}
        handleChangeWorker={handleChangeWorker}
        handleReportVisit={handleReportVisit}
        handleSkipVisit={handleSkipVisitConfirmation}
        handleRateVisit={actionHandleRateVisit}
      />
    );
  }

  if (showSkipVisitSurveySuccess) {
    return <VisitSurveySuccessModal />;
  }

  return null;
}
